/**
 * The Android hardware Back button, bridged onto browser history (D02).
 *
 * Registering a listener through `onBackButton` REPLACES Android's default
 * Back (close the app), so the handler must always do something. The choice
 * is the pure `decideBack`:
 *
 * 1. An ephemeral Base UI popup (Menu, Popover, Select) is open → dispatch
 *    Escape to it and touch nothing else. Popping history in the same press
 *    would close two layers at once.
 * 2. A screen registered an interceptor through `useBackIntercept` → call it,
 *    and nothing else. That is how a screen whose own steps are component
 *    state (the sign-in code step, the onboarding stepper) makes Back mean
 *    "one step back" rather than "leave the app".
 * 3. A sheet or overlay is on top → `history.back()` when this app pushed the
 *    entry behind, otherwise the cold-close REPLACE without that layer.
 * 4. Only a screen, with in-app history behind → `history.back()`.
 * 5. A screen other than Today, opened cold → Today, with REPLACE.
 * 6. Today, opened cold → `exitApp()`.
 *
 * THE EPHEMERAL POPUP CONTRACT (shared with T14, which owns `src/ui/overlay`;
 * it is a DOM contract so `src/app` never imports UI code):
 * - a popup wrapper that is NOT a URL layer puts `data-ephemeral` on its popup
 *   element;
 * - Base UI sets `data-open` on an open popup;
 * - so "a popup is open" is `document.querySelector("[data-ephemeral][data-open]")`;
 * - and closing it is an Escape `keydown` dispatched to
 *   `document.activeElement ?? document.body`, which Base UI handles itself.
 *
 * That pair marks a REAL popup and nothing else — T14's `Menu`, `Popover` and
 * an `ephemeral` `Sheet` (T45). It is read by things that are not this bridge
 * (the trainer's Escape rule in `features/coach/escape-rule.ts`, the
 * onboarding stepper's Escape in `features/onboarding/intake.tsx`), so
 * a marker planted to borrow this bridge's behaviour shows every one of them a
 * popup that does not exist. A screen that wants the press uses
 * `useBackIntercept`.
 */
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  type BackGestureHandlers,
  backGestureAvailable,
  exitApp,
  isNative,
  onBackButton,
  onBackGesture,
  releaseAppBackHandler,
  setBackGestureEnabled,
} from "../native";
import {
  closeFallbackFor,
  hasInAppHistory,
  type LayerKind,
  mergeSearch,
  nextOrder,
  topLayer,
} from "./layers";
import { findTarget, type Preview, startPreview } from "./predictive-back";
import { type Screen, type Search, screenOf } from "./search";

export const EPHEMERAL_OPEN_SELECTOR = "[data-ephemeral][data-open]";

export interface BackState {
  /** A Base UI popup marked `data-ephemeral` is open. */
  readonly ephemeralOpen: boolean;
  /** This app pushed the history entry behind the current one. */
  readonly inAppHistory: boolean;
  /** A mounted screen claimed the press through `useBackIntercept`. */
  readonly intercepted: boolean;
  readonly screen: Screen;
  readonly top: LayerKind;
}

export type BackAction =
  | { kind: "escape" }
  | { kind: "intercept" }
  | { kind: "history-back" }
  | { kind: "close-layer"; layer: Exclude<LayerKind, "screen"> }
  | { kind: "today" }
  | { kind: "exit" };

export function decideBack(state: BackState): BackAction {
  if (state.ephemeralOpen) {
    return { kind: "escape" };
  }
  if (state.intercepted) {
    return { kind: "intercept" };
  }
  if (state.inAppHistory) {
    return { kind: "history-back" };
  }
  if (state.top !== "screen") {
    return { kind: "close-layer", layer: state.top };
  }
  if (state.screen !== "today") {
    return { kind: "today" };
  }
  return { kind: "exit" };
}

export interface BackIntercept {
  /** Read at press time: while it answers false, the entry is skipped. */
  readonly enabled: () => boolean;
  readonly onBack: () => void;
}

/**
 * Mount order, so the LAST screen to mount wins. Not React state: the bridge
 * is a native listener outside render, and it reads this at press time.
 */
const intercepts: BackIntercept[] = [];

/**
 * Told whenever an interceptor comes, goes or toggles: the predictive Back
 * keeps the shell's callback enabled only while Back has somewhere to go
 * (`D193`), and an interceptor is somewhere.
 */
const interceptListeners = new Set<() => void>();

function interceptsChanged(): void {
  for (const listener of interceptListeners) {
    listener();
  }
}

export function onInterceptChange(listener: () => void): () => void {
  interceptListeners.add(listener);
  return () => interceptListeners.delete(listener);
}

/** Adds an entry; the returned function removes that same entry, once. */
export function registerBackIntercept(intercept: BackIntercept): () => void {
  intercepts.push(intercept);
  interceptsChanged();
  let removed = false;
  return () => {
    if (removed) {
      return;
    }
    removed = true;
    const at = intercepts.lastIndexOf(intercept);
    if (at !== -1) {
      intercepts.splice(at, 1);
    }
    interceptsChanged();
  };
}

/** The innermost ENABLED interceptor, or null when no screen claims Back. */
export function topBackIntercept(): (() => void) | null {
  const top = [...intercepts].reverse().find((entry) => entry.enabled());
  return top === undefined ? null : top.onBack;
}

/**
 * While `enabled`, the hardware Back calls `onBack` instead of the default
 * decision — for a screen whose own steps are component state rather than a
 * URL. An open ephemeral popup still closes first. Web: nothing happens,
 * `onBackButton` is a no-op off-device.
 *
 * Registered once per mount, so the order is mount order and a screen that
 * toggles `enabled` does not jump over one mounted above it. Both `enabled`
 * and `onBack` are read from the ref INSIDE the registered functions, at press
 * time — never in a `useCallback` body, which React Compiler refuses to lower
 * (`T-168`).
 */
export function useBackIntercept(enabled: boolean, onBack: () => void): void {
  const latest = useRef({ enabled, onBack });
  useEffect(() => {
    const toggled = latest.current.enabled !== enabled;
    latest.current = { enabled, onBack };
    if (toggled) {
      interceptsChanged();
    }
  }, [enabled, onBack]);
  useEffect(
    () =>
      registerBackIntercept({
        enabled: () => latest.current.enabled,
        onBack: () => latest.current.onBack(),
      }),
    []
  );
}

function isEphemeralOpen(): boolean {
  return document.querySelector(EPHEMERAL_OPEN_SELECTOR) !== null;
}

function dispatchEscape(): void {
  const target = document.activeElement ?? document.body;
  target.dispatchEvent(
    new KeyboardEvent("keydown", {
      bubbles: true,
      cancelable: true,
      code: "Escape",
      key: "Escape",
    })
  );
}

type AppRouter = ReturnType<typeof useRouter>;

/** What Back would do right now, read at press time from the router. */
interface BackReading {
  readonly action: BackAction;
  readonly intercept: (() => void) | null;
  readonly screen: Screen;
  readonly top: LayerKind;
}

function readBack(router: AppRouter): BackReading {
  // Read at press time, never from a render closure.
  const { location } = router.state;
  const search = location.search as Search;
  const intercept = topBackIntercept();
  const screen = screenOf(search);
  const top = topLayer(search, location.state.mpgOrder);
  const action = decideBack({
    ephemeralOpen: isEphemeralOpen(),
    inAppHistory: hasInAppHistory(location.state),
    intercepted: intercept !== null,
    screen,
    top,
  });
  return { action, intercept, screen, top };
}

/** Does what `decideBack` decided — for the button and the gesture alike. */
function runBackAction(router: AppRouter, reading: BackReading): void {
  const { action, intercept } = reading;
  switch (action.kind) {
    case "escape":
      dispatchEscape();
      return;
    case "intercept":
      intercept?.();
      return;
    case "history-back":
      router.history.back();
      return;
    case "close-layer": {
      const fallback = closeFallbackFor(action.layer);
      router.navigate({
        to: "/",
        replace: true,
        search: (prev: Search) => mergeSearch(prev, fallback),
        state: (prev) => ({
          ...prev,
          mpgOrder: nextOrder(prev.mpgOrder ?? [], fallback),
        }),
      });
      return;
    }
    case "today":
      router.navigate({
        to: "/",
        replace: true,
        search: (prev: Search) => mergeSearch(prev, closeFallbackFor("screen")),
        state: true,
      });
      return;
    default:
      exitApp().catch(() => undefined);
  }
}

/**
 * The gesture's four events, wired to a preview and to the same action the
 * button runs. One preview at a time: a new start cancels a stale one.
 */
function gestureHandlers(router: AppRouter): BackGestureHandlers {
  let preview: Preview | null = null;
  return {
    onStarted: (event) => {
      preview?.cancel();
      const { action, top, screen } = readBack(router);
      const target = findTarget(action, top, screen);
      preview = target === null ? null : startPreview(target);
      preview?.progress(event.progress, event.edge);
    },
    onProgressed: (event) => {
      preview?.progress(event.progress, event.edge);
    },
    onCancelled: () => {
      preview?.cancel();
      preview = null;
    },
    onInvoked: () => {
      const reading = readBack(router);
      preview?.commit();
      preview = null;
      runBackAction(router, reading);
    },
  };
}

/**
 * Keeps the shell's Back callback enabled exactly while the app has a use for
 * Back. At Today with nothing open (`decideBack` → exit) it is handed to the
 * system, which plays Android's back-to-home animation; anything that changes
 * the answer — a navigation, a popup opening, an interceptor — re-reads it.
 */
function syncGestureOwnership(router: AppRouter): () => void {
  let frame = 0;
  let owned: boolean | null = null;
  const sync = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const wanted = readBack(router).action.kind !== "exit";
      if (wanted !== owned) {
        owned = wanted;
        setBackGestureEnabled(wanted).catch(() => undefined);
      }
    });
  };
  sync();
  const stopRouter = router.subscribe("onResolved", sync);
  const popups = new MutationObserver(sync);
  popups.observe(document.body, {
    attributeFilter: ["data-open"],
    attributes: true,
    childList: true,
    subtree: true,
  });
  const stopIntercepts = onInterceptChange(sync);
  return () => {
    cancelAnimationFrame(frame);
    stopRouter();
    popups.disconnect();
    stopIntercepts();
  };
}

declare global {
  interface Window {
    /** DEV only: drives the predictive Back from a desktop browser (`D193`). */
    __mpgBackGesture?: BackGestureHandlers;
  }
}

/**
 * Mounts the bridge. In a shell with the `BackGesture` plugin, Back is the
 * predictive gesture; in an older shell, `@capacitor/app`'s button. Web:
 * neither — the browser owns Back — but a DEV build exposes the gesture's
 * handlers on `window.__mpgBackGesture`, so a desktop browser can drive the
 * preview the way the phone does.
 */
export function useAndroidBackButton(): void {
  const router = useRouter();
  useEffect(() => {
    if (!backGestureAvailable()) {
      const stopButton = onBackButton(() =>
        runBackAction(router, readBack(router))
      );
      if (import.meta.env.DEV && !isNative()) {
        window.__mpgBackGesture = gestureHandlers(router);
      }
      return () => {
        stopButton();
        if (import.meta.env.DEV) {
          window.__mpgBackGesture = undefined;
        }
      };
    }
    releaseAppBackHandler().catch(() => undefined);
    const stopGesture = onBackGesture(gestureHandlers(router));
    const stopSync = syncGestureOwnership(router);
    return () => {
      stopGesture();
      stopSync();
      setBackGestureEnabled(false).catch(() => undefined);
    };
  }, [router]);
}
